import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

export type WalkthroughStatus = "idle" | "offered" | "running" | "completed" | "abandoned";

interface WalkthroughState {
	activeTourId: string | null;
	conceptId: string | null;
	dismissible: boolean;
	mutationSignal: string | null;
	status: WalkthroughStatus;
	stepIndex: number;
}

const initialState: WalkthroughState = {
	activeTourId: null,
	conceptId: null,
	dismissible: false,
	mutationSignal: null,
	status: "idle",
	stepIndex: 0
};

export const walkthroughSlice = createSlice({
	initialState,
	name: "walkthrough",
	reducers: {
		offerTour: (state, action: PayloadAction<{ tourId: string }>) => {
			state.activeTourId = action.payload.tourId;
			state.status = "offered";
			state.stepIndex = 0;
			state.mutationSignal = null;
		},
		startTour: (state, action: PayloadAction<{ conceptId?: string; tourId: string; dismissible?: boolean }>) => {
			state.activeTourId = action.payload.tourId;
			state.conceptId = action.payload.conceptId ?? null;
			state.dismissible = action.payload.dismissible ?? false;
			state.status = "running";
			state.stepIndex = 0;
			state.mutationSignal = null;
		},
		advanceStep: (state) => {
			state.stepIndex += 1;
			state.mutationSignal = null;
		},
		goBack: (state) => {
			if (state.stepIndex > 0) state.stepIndex -= 1;
			state.mutationSignal = null;
		},
		signalMutationSuccess: (state, action: PayloadAction<{ signalId: string }>) => {
			state.mutationSignal = action.payload.signalId;
		},
		skipTour: (state) => {
			if (state.status !== "running") return initialState;
			state.status = "abandoned";
			state.mutationSignal = null;
		},
		completeTour: (state) => {
			state.status = "completed";
			state.mutationSignal = null;
		},
		resetTour: () => initialState
	}
});

export const { offerTour, startTour, advanceStep, goBack, signalMutationSuccess, skipTour, completeTour, resetTour } =
	walkthroughSlice.actions;

export default walkthroughSlice.reducer;
